// /frontend/dashboard/src/router/modules/missionRoutes.js
// Mission route module for realtime and mission-centric dashboard pages.
// English explanations are followed by Thai explanations for route-design validation and debugging.

import { debugLog } from '../../utils/debugGate.js';

const ROUTE_DEBUG_NAMESPACE = '[router/missionRoutes]';

function emitMissionRouteDebug(label, payload) {
  debugLog(label, payload);
}

// Static import registry — Vite can statically analyze these at build time.
// English: All component imports must be literal string paths inside this registry.
//          Never use runtime variable strings inside import() — Vite cannot bundle them.
// ไทย: import ทุกตัวต้องเป็น literal string path ใน registry นี้
//      ห้ามใช้ variable string ใน import() เพราะ Vite จะไม่สร้าง chunk สำหรับ production
const PAGE_REGISTRY = {
  DashboardPage:        () => import('../../pages/DashboardPage.vue'),
  ZoneBuilderPage:      () => import('../../pages/ZoneBuilderPage.vue'),
  MissionsPage:         () => import('../../pages/MissionsPage.vue'),
  MissionMonitorPage:   () => import('../../pages/MissionMonitorPage.vue'),
  MissionPlannerPage:   () => import('../../pages/MissionPlannerPage.vue'),
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // emitMissionRouteDebug(
    //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_START] English: starting lazy import for mission route component | ไทย: เริ่ม lazy import สำหรับ component ของ mission route`,
    //   {
    //     registryKey,
    //     debugLabel,
    //   },
    // );

    try {
      const loader = PAGE_REGISTRY[registryKey];

      if (!loader) {
        throw new Error(
          `${ROUTE_DEBUG_NAMESPACE} No registry entry for key: ${registryKey}. Add a static import() entry to PAGE_REGISTRY.`,
        );
      }

      const module = await loader();

      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // emitMissionRouteDebug(
      //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS] English: mission route component loaded successfully | ไทย: โหลด component ของ mission route สำเร็จ`,
      //   {
      //     registryKey,
      //     debugLabel,
      //     exportKeys: Object.keys(module || {}),
      //     hasDefaultExport: Boolean(module && module.default),
      //   },
      // );

      return module;
    } catch (error) {
      console.error(
        `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR] English: failed to load mission route component | ไทย: โหลด component ของ mission route ไม่สำเร็จ`,
        {
          registryKey,
          debugLabel,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      throw error;
    }
  };
}

function createMissionMeta(meta = {}) {
  const merged = {
    section: 'mission',
    requiresAuth: true,
    requiresAdmin: false,
    layout: 'dashboard',
    navGroup: 'mission',
    debug_en: 'Mission route metadata helps validate whether mission-centric pages are grouped correctly and remain separate from dashboard home and admin-only pages.',
    debug_th: 'metadata ของ mission route ใช้ตรวจว่าหน้าที่เกี่ยวกับภารกิจถูกจัดกลุ่มอย่างถูกต้องและแยกจาก dashboard หลักกับหน้าที่เป็น admin-only หรือไม่',
    ...meta,
  };

  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // emitMissionRouteDebug(
  //   `${ROUTE_DEBUG_NAMESPACE} [META_CREATED] English: created mission route metadata | ไทย: สร้าง metadata ของ mission route แล้ว`,
  //   merged,
  // );

  return merged;
}

const missionRoutes = [
  {
    path: '/missions',
    redirect: '/missions/overview',
    meta: createMissionMeta({
      title: 'Mission Root',
      navLabel: 'Missions',
      routePurpose_en: 'Mission root route redirects to the mission overview page.',
      routePurpose_th: 'route หลักของ mission จะ redirect ไปยังหน้า mission overview',
    }),
  },
  {
    path: '/missions/overview',
    name: 'MissionOverview',
    component: buildLazyPage('MissionsPage', 'MissionsPage'),
    meta: createMissionMeta({
      title: 'Missions',
      navLabel: 'Missions',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Mission list page — shows all missions with Start / Stop / Pause controls.',
      routePurpose_th: 'หน้ารายการ mission แสดงทุก mission พร้อมปุ่ม Start / Stop / Pause',
    }),
  },
  {
    path: '/missions/live',
    name: 'MissionLive',
    component: buildLazyPage('DashboardPage', 'MissionLiveViaDashboardPage'),
    meta: createMissionMeta({
      title: 'Mission Live',
      navLabel: 'Live',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Mission live route focuses on validating realtime mission progress, status changes, and robot-to-mission linkage.',
      routePurpose_th: 'route ของ mission live ใช้ตรวจ progress แบบ realtime, การเปลี่ยนสถานะ และการเชื่อมโยง robot-to-mission',
      aliasOf: 'DashboardHome',
    }),
  },
  {
    path: '/missions/zone-builder',
    name: 'ZoneBuilder',
    component: buildLazyPage('ZoneBuilderPage', 'ZoneBuilderPage'),
    meta: createMissionMeta({
      title: 'Zone Builder',
      navLabel: 'Zone Builder',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Zone Builder lets field operators draw coverage zones and no-go areas on the SLAM map, then assign a robot to start a mission.',
      routePurpose_th: 'Zone Builder ให้ operator วาด coverage zone และ no-go area บน SLAM map แล้ว assign robot เพื่อเริ่ม mission',
    }),
  },
  {
    path: '/missions/:mission_id/monitor',
    name: 'MissionMonitor',
    component: buildLazyPage('MissionMonitorPage', 'MissionMonitorPage'),
    meta: createMissionMeta({
      title: 'Mission Monitor',
      navLabel: 'Monitor',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Live mission monitoring page — shows SLAM map, coverage/no-go zones, robot position, and realtime KPIs for a single mission.',
      routePurpose_th: 'หน้าติดตาม mission แบบ realtime — แสดง SLAM map, coverage/no-go zone, ตำแหน่ง robot และ KPI realtime ของ mission เดียว',
    }),
  },
  {
    path: '/missions/planner',
    name: 'MissionPlanner',
    component: buildLazyPage('MissionPlannerPage', 'MissionPlannerPage'),
    meta: createMissionMeta({
      title: 'Mission Day Planner',
      navLabel: 'Planner',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Multi-stop mission day planner — create/edit plans with stops and road waypoints.',
      routePurpose_th: 'วางแผน mission หลายจุดต่อวัน — สร้าง/แก้ plan พร้อม stops และ waypoints บนถนน',
    }),
  },
  {
    path: '/missions/history',
    name: 'MissionHistory',
    component: buildLazyPage('DashboardPage', 'MissionHistoryViaDashboardPage'),
    meta: createMissionMeta({
      title: 'Mission History',
      navLabel: 'History',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Mission history route is reserved for validating future mission history views while temporarily reusing the main dashboard page.',
      routePurpose_th: 'route ของ mission history ถูกเตรียมไว้เพื่อทดสอบหน้าประวัติภารกิจในอนาคต โดยชั่วคราวยังใช้หน้า dashboard หลักร่วมกัน',
      aliasOf: 'DashboardHome',
    }),
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// emitMissionRouteDebug(
//   `${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED] English: mission route module initialized | ไทย: module ของ mission routes ถูกเตรียมใช้งานแล้ว`,
//   {
//     routeCount: missionRoutes.length,
//     routeNames: missionRoutes.map((route) => route.name || `redirect:${route.path}`),
//     paths: missionRoutes.map((route) => route.path),
//     registryKeys: Object.keys(PAGE_REGISTRY),
//     explanation_en: 'Use this snapshot to verify that mission routes are grouped separately from dashboard, admin, robot, and alert route modules.',
//     explanation_th: 'ใช้ snapshot นี้ตรวจว่า mission routes ถูกแยกออกจาก dashboard, admin, robot และ alert route modules อย่างถูกต้อง',
//   },
// );

export default missionRoutes;
